// react
import React, { useEffect, useState, useRef } from "react";
import PropTypes from 'prop-types';

// third-party
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import axios from 'axios';

// application
import { url, slugify } from '../../services/utils';
import { getCategoryIcon } from '../shared/CategoryIcon';
import BlockHeader from '../shared/BlockHeader';

export default function BlockKategori(props) {
    const { token } = props;
    const location = useLocation();
    const currentPath = location.pathname;
    const [listbank, setListBank] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const categoryBlockRef = useRef(null);

    const isCategoryPage =
        currentPath.startsWith('/shop/category') ||
        currentPath.startsWith('/shop/catalog');

    const handleToggleExpand = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsExpanded((prev) => {
            const next = !prev;
            if (!next && categoryBlockRef.current) {
                const rect = categoryBlockRef.current.getBoundingClientRect();
                if (rect.top < 60) {
                    categoryBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
            return next;
        });
    };

    useEffect(() => {
        let isMounted = true;
        const fetchCategories = async () => {
            try {
                const result = await axios({
                    method: "GET",
                    url: `${process.env.REACT_APP_HOST_API}/kategori/list`,
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (isMounted && result.data && Array.isArray(result.data.response)) {
                    setListBank(result.data.response);
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchCategories();
        return () => {
            isMounted = false;
        };
    }, [token]);

    const fallbackCategories = [
        { id: 1, name: "Alat Pertukangan" },
        { id: 2, name: "Bahan Bangunan" },
        { id: 3, name: "Cat" },
        { id: 4, name: "Dapur" },
        { id: 5, name: "Hardware" },
        { id: 6, name: "Hobi" },
        { id: 7, name: "Kamar Mandi" },
        { id: 8, name: "Lantai & Dinding" },
        { id: 9, name: "Listrik & Pencahayaan" },
        { id: 10, name: "Peralatan Rumah Tangga" },
        { id: 11, name: "Pintu & Jendela" },
        { id: 12, name: "Pipa Saluran Air" },
    ];

    const allCategoryItem = {
        id: 'all',
        name: 'Semua Kategori',
        slug: 'all',
        isAll: true,
        customUrl: '/shop/category',
    };

    const moreCategoryItem = {
        id: 'more',
        name: 'Lihat Semua',
        slug: 'all',
        isMore: true,
        isToggle: true,
        customUrl: '/shop/category',
    };

    const closeCategoryItem = {
        id: 'close',
        name: 'Tutup',
        isClose: true,
        isToggle: true,
    };

    const rawCategories = listbank.length > 0 ? listbank : (loading ? [] : fallbackCategories);
    const displayCategories = rawCategories.length > 0 ? [allCategoryItem, ...rawCategories] : [];

    // Mobile categories:
    // If expanded: Show Semua Kategori + all categories + Tutup tile
    // If collapsed: Show 7 categories + 1 "Lihat Semua" (or "Selengkapnya") tile
    let mobileCategories = [];
    if (isExpanded) {
        mobileCategories = [allCategoryItem, ...rawCategories, closeCategoryItem];
    } else if (rawCategories.length > 7) {
        mobileCategories = [...rawCategories.slice(0, 7), moreCategoryItem];
    } else {
        mobileCategories = displayCategories.length > 0 ? displayCategories : [];
    }

    // Desktop categories:
    // Collapsed: 'Semua Kategori' + 10 categories + 'Lihat Semua' tile (1 row)
    // Expanded: everything + 'Tutup' tile
    let desktopCategories = displayCategories;
    if (displayCategories.length > 12) {
        desktopCategories = isExpanded
            ? [...displayCategories, closeCategoryItem]
            : [...displayCategories.slice(0, 11), moreCategoryItem];
    }

    const isCategoryActive = (category) => {
        if (category.isMore || category.isClose) return false;
        if (category.isAll || category.id === 'all') {
            return currentPath === '/shop/category' || currentPath === '/shop/category/' || currentPath === '/shop/catalog';
        }
        const targetSlug = slugify(category.name);
        return (
            currentPath === `/shop/category/${targetSlug}` ||
            currentPath.endsWith(`/${targetSlug}`) ||
            currentPath.endsWith(`/${category.id}`)
        );
    };

    const categorySkeletons = Array.from({ length: 11 }, (_, index) => (
        <div key={`cat-skel-${index}`} className="category-skeleton-item">
            <div className="skeleton-bone category-skeleton-circle" />
            <div className="skeleton-bone category-skeleton-title" />
        </div>
    ));

    return (
        <div ref={categoryBlockRef} className="block block-kategori">
            <div className="container">
                <div className="d-none d-md-block">
                    <BlockHeader
                        title="Kategori"
                    />
                </div>

                {loading && displayCategories.length === 0 ? (
                    <div className="block-kategori-skeleton">
                        {categorySkeletons}
                    </div>
                ) : (
                    <React.Fragment>
                        {/* Desktop & Tablet Slider */}
                        <div className="block-kategori__slider d-none d-md-block">
                            <div className="block-kategori__grid-desktop">
                                {desktopCategories.map((category, index) => {
                                    const active = isCategoryActive(category);
                                    const itemClasses = classNames('block-kategori__item', {
                                        'block-kategori__item--active': active,
                                        'block-kategori__item--toggle': category.isToggle,
                                        'block-kategori__item--close': category.isClose,
                                    });
                                    const content = (
                                        <React.Fragment>
                                            <div
                                                className={classNames('block-kategori__card', {
                                                    'block-kategori__card--close': category.isClose,
                                                    'block-kategori__card--more': category.isMore,
                                                })}
                                            >
                                                <span className="block-kategori__icon">
                                                    {getCategoryIcon(category.name, 34)}
                                                </span>
                                            </div>
                                            <span className="block-kategori__name" title={category.name}>
                                                {category.name}
                                            </span>
                                        </React.Fragment>
                                    );
                                    return (
                                        <div key={category.id || index} className="block-kategori__grid-cell">
                                            {category.isToggle ? (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className={itemClasses}
                                                    onClick={handleToggleExpand}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            handleToggleExpand(e);
                                                        }
                                                    }}
                                                >
                                                    {content}
                                                </div>
                                            ) : (
                                                <Link
                                                    to={category.customUrl || url.category(category)}
                                                    className={itemClasses}
                                                >
                                                    {content}
                                                </Link>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Mobile Grid (2 rows x 4 items expandable on category page) */}
                        <div className="block-kategori__grid-mobile d-block d-md-none">
                            <div className="row row-cols-4 row-cols-sm-4 no-gutters">
                                {mobileCategories.map((category, index) => {
                                    const active = isCategoryActive(category);
                                    const isExtra = isExpanded && index >= 8;
                                    const isClose = category.isClose;
                                    const isMore = category.isMore;
                                    const isToggle = category.isToggle;

                                    const itemClasses = classNames('block-kategori__item', {
                                        'block-kategori__item--active': active,
                                        'block-kategori__item--toggle': isToggle,
                                        'block-kategori__item--close': isClose,
                                        'block-kategori__item--extra': isExtra,
                                        [`block-kategori__item--extra-${Math.min(index - 7, 16)}`]: isExtra,
                                    });

                                    const itemContent = (
                                        <React.Fragment>
                                            <div
                                                className={classNames('block-kategori__card', {
                                                    'block-kategori__card--close': isClose,
                                                    'block-kategori__card--more': isMore,
                                                })}
                                            >
                                                <span className="block-kategori__icon">
                                                    {getCategoryIcon(category.name, 26)}
                                                </span>
                                            </div>
                                            <span className="block-kategori__name" title={category.name}>
                                                {category.name}
                                            </span>
                                        </React.Fragment>
                                    );

                                    return (
                                        <div key={category.id || index} className="col p-1">
                                            {isToggle ? (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className={itemClasses}
                                                    onClick={(e) => {
                                                        if (isCategoryPage || isClose) {
                                                            handleToggleExpand(e);
                                                        } else if (category.customUrl) {
                                                            // On home page, navigate to category catalog
                                                            window.location.href = category.customUrl;
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            handleToggleExpand(e);
                                                        }
                                                    }}
                                                >
                                                    {itemContent}
                                                </div>
                                            ) : (
                                                <Link
                                                    to={category.customUrl || url.category(category)}
                                                    className={itemClasses}
                                                >
                                                    {itemContent}
                                                </Link>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
}

BlockKategori.propTypes = {
    token: PropTypes.string,
};
